import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const parallaxMove = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(-5px, 5px); }
  50% { transform: translate(5px, -5px); }
  75% { transform: translate(-5px, -5px); }
`;

const COLORS = {
  primary: '#2F4F4F',
  secondary: '#8B7355',
  secondaryLight: 'rgba(139, 115, 85, 0.1)'
};

export const HomeWrapper = styled.div`
  width: 100%;
  scroll-behavior: smooth;

  section {
    scroll-margin-top: 80px;
    transition: opacity 0.4s ease;
  }
`;

export const HeroSection = styled.section`
  position: relative;
  height: 90vh;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #2F4F4F 0%, #1c2f2f 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8%;

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
    height: auto;
    min-height: 100vh;
    padding: 2rem 5%;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(139, 115, 85, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(47, 79, 79, 0.1) 0%, transparent 40%);
    pointer-events: none;
    animation: ${parallaxMove} 15s ease-in-out infinite;
  }
`;

export const HeroImage = styled.img`
  height: 85%;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.2));
  margin-left: 5%;
  order: 2;
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    order: 1;
    height: auto;
    width: 85%;
    max-width: 450px;
    margin: 2rem auto;
    display: block;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15));
  }

  @media (max-width: 480px) {
    width: 95%;
    max-width: 400px;
    margin: 1.5rem auto;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 650px;
  margin-right: 5%;
  order: 1;
  background: rgba(255, 255, 255, 0.03);
  padding: 3rem;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin: 0;
    padding: 2rem;
    width: 100%;
    max-width: 100%;
    order: 2;
    background: rgba(255, 255, 255, 0.05);
    margin-top: -2rem;
  }
`;

export const MainTitle = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  color: #FFFFFF;
  line-height: 1.1;
  position: relative;
  
  span {
    display: block;
    background: linear-gradient(90deg, #8B7355, #D4B996, #8B7355);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${shine} 3s linear infinite;
    font-size: 2.2rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
    letter-spacing: 1px;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
    text-align: center;
    
    span {
      font-size: 1.8rem;
    }
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
    
    span {
      font-size: 1.5rem;
    }
  }
`;

export const RotatingText = styled.div`
  height: auto;
  min-height: 80px;
  margin: 2rem 0;
  position: relative;
  padding: 0.5rem 0;
  
  &:before, &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(139, 115, 85, 0.5), 
      transparent
    );
  }
  
  &:before { top: 0; }
  &:after { bottom: 0; }

  span {
    display: block;
    padding: 0.5rem 0;
    font-size: 1.8rem;
    font-weight: 500;
    color: #D4B996;
    text-shadow: 0 0 20px rgba(212, 185, 150, 0.3);
    animation: ${float} 3s ease-in-out infinite;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    text-align: center;
    margin: 1.5rem 0;
    min-height: 100px;
    
    span {
      font-size: 1.4rem;
    }
  }
`;

export const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 300;
  text-shadow: none;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

export const CTAButton = styled(motion.button)`
  padding: 1.2rem 2.8rem;
  font-size: 1.2rem;
  background: linear-gradient(135deg, #8B7355 0%, #D4B996 100%);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  box-shadow: 0 10px 20px rgba(139, 115, 85, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(139, 115, 85, 0.4);
    background: linear-gradient(135deg, #D4B996 0%, #8B7355 100%);
  }
`;

export const SecondaryButton = styled(CTAButton)`
  background: transparent;
  border: 2px solid #8B7355;
  color: #D4B996;
  box-shadow: none;

  &:hover {
    background: rgba(139, 115, 85, 0.1);
    border-color: #D4B996;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

export const HeroTextContent = styled.div`
  flex: 0.8;
  max-width: 550px;
  color: ${props => props.theme.colors.white};
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 1;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
  }
`;

export const HeroImageWrapper = styled.div`
  flex: 1.2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  padding-right: 2rem;
  margin-left: -4rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const BusinessName = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  margin-bottom: 0.5rem;
  line-height: 1.1;
  animation: ${fadeIn} 0.8s ease-out;
  background: linear-gradient(
    to right,
    ${props => props.theme.colors.white} 0%,
    ${props => props.theme.colors.brown} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  font-family: ${props => props.theme.fonts.title};
  font-weight: ${props => props.theme.fontWeights.black};
`;

export const HeroTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.5rem);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  animation: ${fadeIn} 0.8s ease-out;
  animation-delay: 0.1s;
  color: ${props => props.theme.colors.white};
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  font-family: ${props => props.theme.fonts.title};
  font-weight: ${props => props.theme.fontWeights.bold};
  opacity: 0;
  animation-fill-mode: forwards;
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  max-width: 600px;
  margin-bottom: 2rem;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  color: ${props => props.theme.colors.white};
`;

export const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.colors.white};
`;

export const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
`;

export const FeatureCard = styled.div`
  text-align: center;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  border: 1px solid rgba(139, 115, 85, 0.1);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #8B7355, #D4B996);
    transform: scaleX(0);
    transition: transform 0.4s ease;
    transform-origin: right;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

    &:before {
      transform: scaleX(1);
      transform-origin: left;
    }

    svg {
      transform: scale(1.1) rotate(5deg);
      color: #8B7355;
    }
  }

  svg {
    font-size: 3.5rem;
    margin-bottom: 2rem;
    color: #2F4F4F;
    transition: all 0.4s ease;
  }
`;

export const FeatureTitle = styled.h3`
  color: ${props => props.theme.colors.darkGreen};
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: 1rem;
`;

export const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: ${props => props.theme.fontSizes.medium};
  line-height: 1.6;
`;

export const StatsSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(139, 115, 85, 0.2), transparent);
  }
`;

export const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  padding: 2rem;
`;

export const StatItem = styled.div`
  text-align: center;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(139, 115, 85, 0.1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  h2 {
    font-size: 3.5rem;
    margin-bottom: 0.5rem;
    font-weight: 800;
    background: linear-gradient(135deg, #8B7355, #D4B996);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    line-height: 1;
  }

  p {
    font-size: 1.2rem;
    color: #2F4F4F;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    
    h2 {
      font-size: 2.8rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

export const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.colors.white};
`;

export const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.darkGreen};
  font-size: ${props => props.theme.fontSizes.huge};
  text-align: center;
  margin-bottom: 3rem;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: ${props => props.theme.colors.brown};
    border-radius: 2px;
  }
`;

export const TestimonialsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

export const TestimonialCard = styled.div`
  background: ${props => props.theme.colors.white};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.medium};
  position: relative;

  &:before {
    content: '"';
    position: absolute;
    top: -1rem;
    right: 2rem;
    font-size: 5rem;
    color: ${props => props.theme.colors.brown};
    opacity: 0.2;
  }
`;

export const TestimonialText = styled.p`
  color: ${props => props.theme.colors.darkGreen};
  font-size: ${props => props.theme.fontSizes.medium};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

export const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const AuthorImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

export const AuthorInfo = styled.div`
  h4 {
    color: ${props => props.theme.colors.darkGreen};
    font-size: ${props => props.theme.fontSizes.medium};
    margin-bottom: 0.25rem;
  }

  p {
    color: ${props => props.theme.colors.gray};
    font-size: ${props => props.theme.fontSizes.small};
  }
`;

export const FAQSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.colors.lightGray};
`;

export const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

export const FAQItem = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(139, 115, 85, 0.1);

  &:hover {
    transform: translateX(-5px);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }
`;

export const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${props => props.theme.colors.darkGreen};
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes.large};

  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

export const FAQAnswer = styled.div`
  color: #2F4F4F;
  font-size: 1.1rem;
  line-height: 1.8;
  margin-top: ${props => props.isOpen ? '1.5rem' : '0'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  overflow: hidden;
  transition: all 0.4s ease;
  padding-right: 1rem;
  border-right: 3px solid ${props => props.isOpen ? '#8B7355' : 'transparent'};
`;

export const CaseStudiesSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.colors.background};
`;

export const CaseStudiesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

export const CaseStudyCard = styled(motion.div)`
  background: ${props => props.theme.isDarkMode ? '#2a2a2a' : '#fff'};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  border: 1px solid ${props => props.theme.colors.border};
`;

export const CaseStudyImage = styled.div`
  height: 200px;
  background: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
`;

export const CaseStudyContent = styled.div`
  padding: 2rem;
`;

export const CaseStudyTitle = styled.h3`
  margin-bottom: 1rem;
  color: ${COLORS.primary};
`;

export const SavingsBadge = styled.div`
  background: ${COLORS.secondaryLight};
  color: ${COLORS.secondary};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  display: inline-block;
  font-weight: bold;
  margin-bottom: 1rem;
`;

export const TrustBarSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.isDarkMode ? '#1a1a1a' : '#f9f9f9'};
  overflow: hidden;
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

export const TrustLogosTrack = styled.div`
  display: flex;
  width: calc(250px * 12);
  animation: ${scroll} 30s linear infinite;
  
  &:hover {
    animation-play-state: paused;
  }
`;

export const TrustLogo = styled.div`
  width: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.gray};
  opacity: 0.6;
  filter: grayscale(100%);
  transition: all 0.3s ease;

  &:hover {
    opacity: 1;
    filter: grayscale(0);
  }
`;
